import React, { useContext, useEffect, useRef, useState } from "react";
import { IonButton, IonChip, IonCol, IonIcon, IonInput, IonItem, IonLabel, IonRow } from "@ionic/react";
import { trash } from "ionicons/icons";
import BasicModal from "../../Modals/BasicModal";
import { PersonContext } from "../../../context/PersonContext";
import { Membership } from "../../../models";
import { CreateMembershipInput, UpdateMembershipInput } from "../../../API";
import {
    createMembership,
    deleteMembership,
    getMembershipsByHorseId,
    getMembershipsByOwnerId,
    getMembershipsByRiderId,
    getMembershipsByTrainerId,
    updateMembership,
} from "../../../utilities/membership/Membership";

export type MembershipEntityType = "horse" | "rider" | "owner" | "trainer";

export type MembershipTypeKey = "USEF" | "USHJA" | "FEI" | "EC" | "TIP" | "other";

type PresetMembershipTypeKey = Exclude<MembershipTypeKey, "other">;

const getPresetMembershipOptions = (
    entityType: MembershipEntityType
): { key: PresetMembershipTypeKey; label: string; membershipName: string }[] => {
    const isHorse = entityType === "horse";
    return [
        { key: "USEF", label: "USEF", membershipName: isHorse ? "USEF Horse" : "USEF" },
        { key: "USHJA", label: "USHJA", membershipName: isHorse ? "USHJA Horse" : "USHJA" },
        { key: "FEI", label: "FEI", membershipName: isHorse ? "FEI Horse" : "FEI" },
        { key: "EC", label: "EC", membershipName: isHorse ? "EC Horse" : "EC" },
        { key: "TIP", label: "TIP", membershipName: "TIP" },
    ];
};

const OTHER_MEMBERSHIP_OPTION = { key: "other" as const, label: "Other", membershipName: "" };

/** Match stored membership names to a chip key (covers both "USEF" and "USEF Horse"). */
const resolveMembershipTypeKey = (membershipName: string): PresetMembershipTypeKey | null => {
    const normalized = membershipName.trim().toLowerCase();
    if (normalized === "usef" || normalized === "usef horse") return "USEF";
    if (normalized === "ushja" || normalized === "ushja horse") return "USHJA";
    if (normalized === "fei" || normalized === "fei horse") return "FEI";
    if (normalized === "ec" || normalized === "ec horse") return "EC";
    if (normalized === "tip" || normalized === "tip horse") return "TIP";
    return null;
};

interface MembershipDraft {
    id: string;
    otherName: string;
    existingMembershipId?: string;
}

type MembershipField =
    | { kind: "preset"; key: PresetMembershipTypeKey; draft: MembershipDraft }
    | { kind: "other"; draftId: string; draft: MembershipDraft };

export interface AddMembershipModalProps {
    isOpen: boolean;
    entityType: MembershipEntityType;
    entityId?: string | null;
    onClose: () => void;
    onSaved?: (memberships: Membership[]) => void;
}

const emptyDraft = (): MembershipDraft => ({ id: "", otherName: "" });

const createDraftId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const getMembershipsForEntity = async (entityType: MembershipEntityType, entityId: string) => {
    switch (entityType) {
        case "horse":
            return getMembershipsByHorseId(entityId);
        case "rider":
            return getMembershipsByRiderId(entityId);
        case "owner":
            return getMembershipsByOwnerId(entityId);
        case "trainer":
            return getMembershipsByTrainerId(entityId);
    }
};

const buildCreateInput = (
    entityType: MembershipEntityType,
    entityId: string,
    personId: string,
    name: string,
    membershipId: string
): CreateMembershipInput => {
    const base: CreateMembershipInput = {
        personId,
        name,
        membershipId,
    };

    switch (entityType) {
        case "horse":
            return { ...base, horseId: entityId };
        case "rider":
            return { ...base, riderId: entityId };
        case "owner":
            return { ...base, ownerId: entityId };
        case "trainer":
            return { ...base, trainerId: entityId };
    }
};

const entityLabel = (entityType: MembershipEntityType): string => {
    switch (entityType) {
        case "horse":
            return "horse";
        case "rider":
            return "rider";
        case "owner":
            return "owner";
        case "trainer":
            return "trainer";
    }
};

const findMatchingMembership = (memberships: Membership[], field: MembershipField, membershipName: string): Membership | undefined => {
    if (field.draft.existingMembershipId) {
        return memberships.find((membership) => membership.id === field.draft.existingMembershipId);
    }

    if (field.kind === "preset") {
        return memberships.find((membership) => resolveMembershipTypeKey(membership.name) === field.key);
    }

    return memberships.find((membership) => !resolveMembershipTypeKey(membership.name) && membership.name === membershipName);
};

const AddMembershipModal: React.FC<AddMembershipModalProps> = ({ isOpen, entityType, entityId, onClose, onSaved }) => {
    const user = useContext(PersonContext);
    const presetOptions = getPresetMembershipOptions(entityType);

    const [fields, setFields] = useState<MembershipField[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const removedPresetDraftsRef = useRef<Partial<Record<PresetMembershipTypeKey, MembershipDraft>>>({});

    const resetDraftTracking = () => {
        removedPresetDraftsRef.current = {};
    };

    useEffect(() => {
        if (!isOpen) return;

        const loadExisting = async () => {
            setError("");
            setFields([]);
            resetDraftTracking();

            if (!entityId) return;

            setIsLoading(true);
            const queryResult = await getMembershipsForEntity(entityType, entityId);
            if (queryResult.isSuccess) {
                const memberships: Membership[] = queryResult.result || [];
                const nextFields: MembershipField[] = [];

                memberships.forEach((membership) => {
                    const presetKey = resolveMembershipTypeKey(membership.name);
                    const draft: MembershipDraft = {
                        id: membership.membershipId || "",
                        otherName: presetKey ? "" : membership.name || "",
                        existingMembershipId: membership.id,
                    };

                    if (presetKey) {
                        if (!nextFields.some((field) => field.kind === "preset" && field.key === presetKey)) {
                            nextFields.push({ kind: "preset", key: presetKey, draft });
                        }
                        return;
                    }

                    nextFields.push({ kind: "other", draftId: createDraftId(), draft });
                });

                setFields(nextFields);
            }
            setIsLoading(false);
        };

        loadExisting();
    }, [isOpen, entityType, entityId]);

    const togglePresetType = (key: PresetMembershipTypeKey) => {
        setError("");
        setFields((prev) => {
            const existingIndex = prev.findIndex((field) => field.kind === "preset" && field.key === key);
            if (existingIndex >= 0) {
                const removedField = prev[existingIndex];
                if (removedField.kind === "preset") {
                    removedPresetDraftsRef.current[key] = removedField.draft;
                }
                return prev.filter((_, index) => index !== existingIndex);
            }

            const restoredDraft = removedPresetDraftsRef.current[key] || emptyDraft();
            delete removedPresetDraftsRef.current[key];

            return [{ kind: "preset", key, draft: restoredDraft }, ...prev];
        });
    };

    const addOtherMembership = () => {
        setError("");
        setFields((prev) => [{ kind: "other", draftId: createDraftId(), draft: emptyDraft() }, ...prev]);
    };

    const updatePresetDraft = (key: PresetMembershipTypeKey, patch: Partial<MembershipDraft>) => {
        setFields((prev) =>
            prev.map((field) => (field.kind === "preset" && field.key === key ? { ...field, draft: { ...field.draft, ...patch } } : field))
        );
    };

    const updateOtherDraft = (draftId: string, patch: Partial<MembershipDraft>) => {
        setFields((prev) =>
            prev.map((field) => (field.kind === "other" && field.draftId === draftId ? { ...field, draft: { ...field.draft, ...patch } } : field))
        );
    };

    const removeField = async (field: MembershipField) => {
        setError("");

        if (field.draft.existingMembershipId) {
            setIsLoading(true);
            const deleteResult = await deleteMembership({ id: field.draft.existingMembershipId });
            setIsLoading(false);
            if (!deleteResult.isSuccess) {
                setError(deleteResult.message || "Could not delete the membership.");
                return;
            }
            if (onSaved) onSaved([]);
        }

        if (field.kind === "preset") {
            delete removedPresetDraftsRef.current[field.key];
        }

        setFields((prev) =>
            prev.filter((current) => {
                if (field.kind === "preset") {
                    return !(current.kind === "preset" && current.key === field.key);
                }
                return !(current.kind === "other" && current.draftId === field.draftId);
            })
        );
    };

    const renderOtherMembershipField = (field: Extract<MembershipField, { kind: "other" }>) => (
        <IonRow key={field.draftId} className="ion-justify-content-center">
            <IonCol sizeXs="12" sizeMd="10">
                <IonRow className="ion-align-items-end">
                    <IonCol size="6" className="p-0 pl-0 pr-1">
                        <IonItem color="white">
                            <IonLabel position="stacked" className="ion-text-wrap">Organization Name</IonLabel>
                            <IonInput
                                type="text"
                                value={field.draft.otherName}
                                placeholder="Membership name"
                                onIonChange={(e) => updateOtherDraft(field.draftId, { otherName: e.detail.value || "" })}
                            />
                        </IonItem>
                    </IonCol>
                    <IonCol size="6" className="p-0 pr-0 pl-1">
                        <IonItem color="white">
                            <IonLabel position="stacked" className="ion-text-wrap">Member ID Number</IonLabel>
                            <div className="d-flex align-items-center w-100">
                                <IonInput
                                    type="text"
                                    value={field.draft.id}
                                    placeholder="ID number"
                                    onIonChange={(e) => updateOtherDraft(field.draftId, { id: e.detail.value || "" })}
                                />
                                <IonButton
                                    fill="clear"
                                    color="danger"
                                    disabled={isLoading}
                                    aria-label="Remove membership"
                                    className="m-0 ion-no-padding"
                                    style={{ minHeight: "unset", height: "auto", flexShrink: 0 }}
                                    onClick={() => removeField(field)}>
                                    <IonIcon icon={trash} className="card-icon-size-2 text-danger" />
                                </IonButton>
                            </div>
                        </IonItem>
                    </IonCol>
                </IonRow>
            </IonCol>
        </IonRow>
    );

    const renderIdField = (
        draft: MembershipDraft,
        label: string,
        placeholder: string,
        onIdChange: (value: string) => void,
        onRemove: () => void
    ) => (
        <IonRow className="ion-justify-content-center">
            <IonCol sizeXs="12" sizeMd="10">
                <IonItem color="white">
                    <IonLabel position="stacked">{label}</IonLabel>
                    <div className="d-flex align-items-center w-100">
                        <IonInput type="text" value={draft.id} placeholder={placeholder} onIonChange={(e) => onIdChange(e.detail.value || "")} />
                        <IonButton
                            fill="clear"
                            color="danger"
                            disabled={isLoading}
                            aria-label={`Remove ${label}`}
                            className="m-0 ion-no-padding"
                            style={{ minHeight: "unset", height: "auto", flexShrink: 0 }}
                            onClick={onRemove}>
                            <IonIcon icon={trash} className="card-icon-size-2 text-danger" />
                        </IonButton>
                    </div>
                </IonItem>
            </IonCol>
        </IonRow>
    );

    const handleClose = () => {
        if (isLoading) return;
        setError("");
        onClose();
    };

    const handleSave = async () => {
        if (!entityId) {
            setError(`Please save the ${entityLabel(entityType)} before adding memberships.`);
            return;
        }
        if (fields.length === 0) {
            setError("Select at least one membership type.");
            return;
        }

        for (const field of fields) {
            if (!field.draft.id.trim()) {
                const label =
                    field.kind === "other"
                        ? field.draft.otherName.trim() || "Other"
                        : presetOptions.find((option) => option.key === field.key)?.label || field.key;
                setError(`Please enter an ID number for ${label}.`);
                return;
            }
            if (field.kind === "other" && !field.draft.otherName.trim()) {
                setError("Please enter a name for the other membership.");
                return;
            }
        }

        setIsLoading(true);
        setError("");

        const savedMemberships: Membership[] = [];

        const existingQueryResult = await getMembershipsForEntity(entityType, entityId);
        let existingMemberships: Membership[] = existingQueryResult.isSuccess ? existingQueryResult.result || [] : [];

        for (const field of fields) {
            const draft = field.draft;
            const membershipName =
                field.kind === "other" ? draft.otherName.trim() : presetOptions.find((item) => item.key === field.key)?.membershipName || field.key;

            const matchedMembership = findMatchingMembership(existingMemberships, field, membershipName);
            const membershipRecordId = draft.existingMembershipId || matchedMembership?.id;

            if (membershipRecordId) {
                const updateInput: UpdateMembershipInput = {
                    id: membershipRecordId,
                    name: membershipName,
                    membershipId: draft.id.trim(),
                };
                const updateResult = await updateMembership(updateInput);
                if (!updateResult.isSuccess) {
                    setError(updateResult.message || `Could not update the ${membershipName} membership.`);
                    setIsLoading(false);
                    return;
                }
                savedMemberships.push(updateResult.result);
                existingMemberships = existingMemberships.map((membership) =>
                    membership.id === membershipRecordId ? updateResult.result : membership
                );
            } else {
                const createInput = buildCreateInput(entityType, entityId, user.id, membershipName, draft.id.trim());
                const createResult = await createMembership(createInput);
                if (!createResult.isSuccess) {
                    setError(createResult.message || `Could not add the ${membershipName} membership.`);
                    setIsLoading(false);
                    return;
                }
                savedMemberships.push(createResult.result);
                existingMemberships = [...existingMemberships, createResult.result];
            }
        }

        setIsLoading(false);
        resetDraftTracking();
        if (onSaved) onSaved(savedMemberships);
        onClose();
    };

    return (
        <BasicModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add a Membership"
            isLoading={isLoading}
            errorMessage={error}
            footerButtons={[
                {
                    text: "Cancel",
                    color: "light",
                    disabled: isLoading,
                    onClick: handleClose,
                },
                {
                    text: "SAVE",
                    loadingText: "Saving...",
                    color: "success",
                    disabled: isLoading || !entityId,
                    onClick: handleSave,
                },
            ]}>
            <>
                <IonRow className="ion-justify-content-center">
                    <IonCol sizeXs="12" sizeMd="10" className="ion-text-center">
                        <h4 className="text-info font-weight-bold">Which memberships would you like to add?</h4>
                    </IonCol>
                </IonRow>

                <IonRow className="ion-justify-content-center">
                    <IonCol sizeXs="12" sizeMd="10" className="d-flex flex-wrap ion-justify-content-center" style={{ gap: "0.5rem" }}>
                        {presetOptions.map((option) => {
                            const isSelected = fields.some((field) => field.kind === "preset" && field.key === option.key);
                            return (
                                <IonChip
                                    key={option.key}
                                    outline={!isSelected}
                                    color={isSelected ? "primary" : "medium"}
                                    className={`m-0 rounded-2 font-weight-bold ${isSelected ? "text-white" : ""}`}
                                    onClick={() => togglePresetType(option.key)}>
                                    <span style={{ paddingRight: "4px" }}>+</span>
                                    {option.label}
                                </IonChip>
                            );
                        })}
                        <IonChip outline color="medium" className="m-0 rounded-2 font-weight-bold" onClick={addOtherMembership}>
                            <span style={{ paddingRight: "4px" }}>+</span>
                            {OTHER_MEMBERSHIP_OPTION.label}
                        </IonChip>
                    </IonCol>
                </IonRow>

                {fields.length > 0 && (
                    <>
                        <hr className="my-3" />
                        {fields.map((field) => {
                            if (field.kind === "other") {
                                return renderOtherMembershipField(field);
                            }

                            const option = presetOptions.find((item) => item.key === field.key);
                            return (
                                <React.Fragment key={field.key}>
                                    {renderIdField(
                                        field.draft,
                                        `${option?.label} ID`,
                                        `Enter ${option?.label} ID`,
                                        (value) => updatePresetDraft(field.key, { id: value }),
                                        () => removeField(field)
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </>
                )}
            </>
        </BasicModal>
    );
};

export default AddMembershipModal;
